import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { TabsContent } from '../../../components/ui/tabs';
import { Button } from '../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { FileUp, Eye, CheckCircle2, AlertTriangle, MinusCircle, Loader2, Lock, TrendingUp, TrendingDown, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { forestaOpsApi } from '../../../lib/api';
import { MonthPeriodHeader } from '../../../components/common/MonthPeriodHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';

const FILTROS = [
  { value: 'todos', label: 'Todos' },
  { value: 'pendiente_revision_gc', label: 'Sin revisar GC' },
  { value: 'esperando_factura', label: 'Esperando factura' },
  { value: 'esperando_comprobante', label: 'Esperando comprobante' },
  { value: 'observado', label: 'Observados' },
  { value: 'completo', label: 'Completos' },
];

const ESTADO_GENERAL = {
  no_aplica: { label: 'No aplica', cls: 'bg-slate-100 text-slate-500 ring-slate-200' },
  pendiente_revision_gc: { label: 'Revisión GC', cls: 'bg-orange-50 text-orange-700 ring-orange-100' },
  esperando_factura: { label: 'Esperando factura', cls: 'bg-blue-50 text-blue-700 ring-blue-100' },
  esperando_comprobante: { label: 'Esperando comprob.', cls: 'bg-indigo-50 text-indigo-700 ring-indigo-100' },
  en_revision: { label: 'En revisión', cls: 'bg-amber-50 text-amber-700 ring-amber-100' },
  observado: { label: 'Observado', cls: 'bg-amber-50 text-amber-700 ring-amber-100' },
  completo: { label: 'Completo', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
};

const REVISION_BADGE = {
  pendiente: { label: 'Pendiente', cls: 'bg-orange-50 text-orange-700 ring-orange-100', Icon: AlertTriangle },
  aprobado: { label: 'Aprobado', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100', Icon: CheckCircle2 },
  observado: { label: 'Observado', cls: 'bg-amber-50 text-amber-700 ring-amber-100', Icon: AlertTriangle },
  no_aplica: { label: 'N/A', cls: 'bg-slate-100 text-slate-500 ring-slate-200', Icon: MinusCircle },
};

const FACT_BADGE = {
  no_aplica: { label: 'N/A', cls: 'bg-slate-100 text-slate-500 ring-slate-200', Icon: MinusCircle },
  no_habilitada: { label: 'Bloqueada', cls: 'bg-slate-100 text-slate-400 ring-slate-200', Icon: Lock },
  falta: { label: 'Falta', cls: 'bg-orange-50 text-orange-700 ring-orange-100', Icon: AlertTriangle },
  subida: { label: 'Subida', cls: 'bg-blue-50 text-blue-700 ring-blue-100', Icon: CheckCircle2 },
  observada: { label: 'Observada', cls: 'bg-amber-50 text-amber-700 ring-amber-100', Icon: AlertTriangle },
  aprobada: { label: 'Aprobada', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100', Icon: CheckCircle2 },
};

const COMP_BADGE = {
  no_aplica: { label: 'N/A', cls: 'bg-slate-100 text-slate-500 ring-slate-200', Icon: MinusCircle },
  no_habilitado: { label: 'Bloqueado', cls: 'bg-slate-100 text-slate-400 ring-slate-200', Icon: Lock },
  falta: { label: 'Falta', cls: 'bg-orange-50 text-orange-700 ring-orange-100', Icon: AlertTriangle },
  subido: { label: 'Subido', cls: 'bg-blue-50 text-blue-700 ring-blue-100', Icon: CheckCircle2 },
  observado: { label: 'Observado', cls: 'bg-amber-50 text-amber-700 ring-amber-100', Icon: AlertTriangle },
  validado: { label: 'Validado', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100', Icon: CheckCircle2 },
};

const fmtMoney = (n) => n == null ? '—' : new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);

const Badge = ({ meta, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-medium ring-1 ${meta.cls}`}>
    {meta.Icon && <meta.Icon className="w-3 h-3" />}
    {meta.label}
  </span>
);

const IconAction = ({ onClick, label, testid, children, tone = 'slate', disabled = false }) => {
  const tones = {
    slate: 'text-slate-500 hover:text-slate-800 hover:bg-slate-100',
    red: 'text-red-500 hover:text-red-700 hover:bg-red-50',
    green: 'text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50',
    amber: 'text-amber-600 hover:text-amber-800 hover:bg-amber-50',
    blue: 'text-blue-600 hover:text-blue-800 hover:bg-blue-50',
  };
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className={`inline-flex items-center justify-center w-5 h-5 rounded ${tones[tone]} disabled:opacity-30 disabled:cursor-not-allowed`}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const UploadLabel = ({ label, onUpload, testid, isUploading, disabled = false }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <label
          data-testid={testid}
          className={`inline-flex items-center justify-center w-5 h-5 rounded text-slate-500 hover:text-slate-800 hover:bg-slate-100 ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileUp className="w-3 h-3" />}
          <input
            type="file"
            disabled={disabled}
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f && !disabled) onUpload(f);
              e.target.value = '';
            }}
          />
        </label>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const ObsDialog = ({ open, onOpenChange, title, onSave, initialObs = '' }) => {
  const [obs, setObs] = useState(initialObs);
  useEffect(() => { if (open) setObs(initialObs); }, [open, initialObs]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <textarea
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          rows={3}
          placeholder="Observación..."
          className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
          data-testid="obs-dialog-input"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => { onSave(obs); onOpenChange(false); }} data-testid="obs-dialog-save">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Helper para mostrar variación en "Mes anterior"
const PrevMontoCell = ({ actual, anterior }) => {
  if (anterior == null) return <span className="text-slate-400">—</span>;
  const diff = actual != null && anterior > 0 ? ((actual - anterior) / anterior) * 100 : null;
  return (
    <div className="text-xs">
      <div className="text-slate-700 whitespace-nowrap">{fmtMoney(anterior)}</div>
      {diff != null && (
        <div className={`flex items-center gap-0.5 text-[10px] ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-600' : 'text-slate-400'}`}>
          {diff > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : diff < 0 ? <TrendingDown className="w-2.5 h-2.5" /> : null}
          {diff > 0 ? '+' : ''}{diff.toFixed(0)}%
        </div>
      )}
    </div>
  );
};

export const TabAlquilerMensualForesta = ({ edificioId, periodo, setSelectedPeriodo, activeTab }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [filtro, setFiltro] = useState('todos');
  const [obsDialog, setObsDialog] = useState(null);
  const [cancelAprobDialog, setCancelAprobDialog] = useState(null);  // unidadId a cancelar aprobación

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await forestaOpsApi.listAlquilerMensual(edificioId, periodo);
      setItems(r.data.items || []);
    } catch (e) { toast.error('Error al cargar alquiler mensual'); }
    finally { setLoading(false); }
  }, [edificioId, periodo]);

  useEffect(() => {
    if (activeTab !== 'factura') return;
    load();
  }, [activeTab, load]);

  const contadores = useMemo(() => {
    const c = { todos: items.length };
    for (const it of items) {
      const eg = it.estado_general;
      c[eg] = (c[eg] || 0) + 1;
    }
    return c;
  }, [items]);

  const itemsFiltrados = useMemo(() => {
    if (filtro === 'todos') return items;
    return items.filter(it => it.estado_general === filtro);
  }, [items, filtro]);

  const setRevision = async (unidadId, estado, observacion = '') => {
    try {
      await forestaOpsApi.upsertRevision(edificioId, unidadId, periodo, { estado, observacion });
      toast.success(`Revisión: ${estado}`);
      load();
    } catch (e) { toast.error('Error al actualizar revisión'); }
  };

  const uploadFactura = async (unidadId, file) => {
    const key = `f-${unidadId}`; setUploadingKey(key);
    try {
      await forestaOpsApi.uploadFactura(edificioId, unidadId, periodo, file);
      toast.success('Factura subida'); load();
    } catch (e) { toast.error('Error al subir factura'); }
    finally { setUploadingKey(null); }
  };

  const patchFactura = async (unidadId, data) => {
    try {
      await forestaOpsApi.patchFactura(edificioId, unidadId, periodo, data);
      toast.success('Factura actualizada'); load();
    } catch (e) { toast.error('Error al actualizar factura'); }
  };

  return (
    <TabsContent value="factura" className="mt-0">
      <div className="space-y-2 flex-1 min-h-0 flex flex-col">
        {setSelectedPeriodo ? (
          <MonthPeriodHeader
            value={periodo}
            onChange={setSelectedPeriodo}
            testid="foresta-header-factura"
            right={
              <>
                <span className="text-xs text-slate-500 ml-1">Filtrar:</span>
                {FILTROS.map(f => (
                  <Button
                    key={f.value}
                    variant={filtro === f.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFiltro(f.value)}
                    className="text-xs h-8"
                    data-testid={`alq-filter-${f.value}`}
                  >
                    {f.label}
                    {contadores[f.value] != null && (
                      <span className="ml-1 opacity-70">({contadores[f.value]})</span>
                    )}
                  </Button>
                ))}
              </>
            }
          />
        ) : (
          /* Fallback: si por algún motivo no llega el setter, renderizar los filtros solos */
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Filtrar:</span>
            {FILTROS.map(f => (
              <Button
                key={f.value}
                variant={filtro === f.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltro(f.value)}
                className="text-xs"
                data-testid={`alq-filter-${f.value}`}
              >
                {f.label}
                {contadores[f.value] != null && (
                  <span className="ml-1 opacity-70">({contadores[f.value]})</span>
                )}
              </Button>
            ))}
          </div>
        )}

        <div className="bg-white rounded-lg border border-slate-200 flex-1 min-h-0 flex flex-col overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : itemsFiltrados.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">No hay registros para este filtro.</div>
          ) : (
            <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full text-sm table-fixed min-h-full">
              <colgroup>
                <col style={{ width: '12%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Unidad</th>
                  <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Propietario</th>
                  <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Inquilino</th>
                  <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Mes ant.</th>
                  <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Alquiler</th>
                  <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Revisión GC</th>
                  <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Factura prop.</th>
                  <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {itemsFiltrados.map(it => {
                  const noContrato = !it.tiene_contrato;
                  const revEstado = it.revision.estado;
                  const factEstado = noContrato ? 'no_aplica' : (revEstado === 'aprobado' ? it.factura.estado : (it.factura.archivo_id ? it.factura.estado : 'no_habilitada'));
                  const gen = ESTADO_GENERAL[it.estado_general] || ESTADO_GENERAL.no_aplica;
                  const revMeta = REVISION_BADGE[revEstado] || REVISION_BADGE.pendiente;
                  const factMeta = FACT_BADGE[factEstado] || FACT_BADGE.falta;

                  return (
                    <tr key={it.unidad_id} className={`border-b border-slate-100 hover:bg-slate-50 align-top ${it.estado_general === 'no_aplica' ? 'opacity-50 bg-slate-50/30' : ''}`} data-testid={`alq-row-${it.unidad_id}`}>
                      <td className="py-2.5 px-3 font-medium text-slate-900 truncate" title={it.unidad_nombre}>
                        <div className="truncate">{it.unidad_nombre}</div>
                        <div className="text-[10px] text-slate-400 capitalize">{it.tipo}</div>
                      </td>
                      <td className="py-2.5 px-3 text-slate-700 truncate" title={it.propietario_nombre}>{it.propietario_nombre || '—'}</td>
                      <td className="py-2.5 px-3 text-slate-700 truncate" title={it.inquilino || ''}>{it.inquilino || '—'}</td>
                      <td className="py-2.5 px-2 text-right" data-testid={`alq-prev-${it.unidad_id}`}>
                        <PrevMontoCell actual={it.monto_alquiler} anterior={it.monto_alquiler_mes_anterior} />
                      </td>
                      <td className="py-2.5 px-2 text-right text-slate-700 whitespace-nowrap text-xs">{fmtMoney(it.monto_alquiler)}</td>

                      {/* Revisión GC: badge arriba, acciones abajo */}
                      <td className="py-2.5 px-2 text-center" data-testid={`alq-rev-${it.unidad_id}`}>
                        <div className="flex flex-col items-center gap-1">
                          <Badge meta={revMeta} testid={`alq-rev-badge-${it.unidad_id}`} />
                          {!noContrato && (
                            <div className="flex items-center gap-0.5">
                              {revEstado !== 'aprobado' && (
                                <IconAction tone="green" label="Aprobar monto" onClick={() => setRevision(it.unidad_id, 'aprobado')} testid={`alq-rev-aprobar-${it.unidad_id}`}>
                                  <CheckCircle2 className="w-3 h-3" />
                                </IconAction>
                              )}
                              {revEstado === 'aprobado' && (
                                <IconAction tone="amber" label="Cancelar aprobación" onClick={() => setCancelAprobDialog(it.unidad_id)} testid={`alq-rev-cancelar-${it.unidad_id}`}>
                                  <RotateCcw className="w-3 h-3" />
                                </IconAction>
                              )}
                              {revEstado !== 'observado' && (
                                <IconAction tone="amber" label="Observar" onClick={() => setObsDialog({ tipo: 'rev', unidad: it })} testid={`alq-rev-observar-${it.unidad_id}`}>
                                  <AlertTriangle className="w-3 h-3" />
                                </IconAction>
                              )}
                            </div>
                          )}
                          {it.revision.observacion && (
                            <div className="text-[10px] text-amber-700 truncate max-w-[100px]" title={it.revision.observacion}>{it.revision.observacion}</div>
                          )}
                        </div>
                      </td>

                      {/* Factura propietario */}
                      <td className="py-2.5 px-2 text-center" data-testid={`alq-fact-${it.unidad_id}`}>
                        <div className="flex flex-col items-center gap-1">
                          <Badge meta={factMeta} testid={`alq-fact-badge-${it.unidad_id}`} />
                          {!(factEstado === 'no_habilitada' || factEstado === 'no_aplica') && (
                            <div className="flex items-center gap-0.5">
                              {it.factura.archivo_id && (
                                <a
                                  href={forestaOpsApi.archivoUrl(it.factura.archivo_id)}
                                  target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center w-5 h-5 rounded text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                                  title="Ver factura"
                                  data-testid={`alq-fact-ver-${it.unidad_id}`}
                                >
                                  <Eye className="w-3 h-3" />
                                </a>
                              )}
                              <UploadLabel
                                label={it.factura.archivo_id ? 'Reemplazar factura' : 'Subir factura'}
                                testid={`alq-fact-upload-${it.unidad_id}`}
                                isUploading={uploadingKey === `f-${it.unidad_id}`}
                                onUpload={(f) => uploadFactura(it.unidad_id, f)}
                              />
                              {it.factura.archivo_id && factEstado !== 'aprobada' && (
                                <IconAction tone="green" label="Aprobar factura" onClick={() => patchFactura(it.unidad_id, { estado: 'aprobada' })} testid={`alq-fact-aprobar-${it.unidad_id}`}>
                                  <CheckCircle2 className="w-3 h-3" />
                                </IconAction>
                              )}
                              {it.factura.archivo_id && factEstado !== 'observada' && (
                                <IconAction tone="amber" label="Observar factura" onClick={() => setObsDialog({ tipo: 'fact', unidad: it })} testid={`alq-fact-observar-${it.unidad_id}`}>
                                  <AlertTriangle className="w-3 h-3" />
                                </IconAction>
                              )}
                            </div>
                          )}
                          {it.factura.observaciones && (
                            <div className="text-[10px] text-amber-700 truncate max-w-[100px]" title={it.factura.observaciones}>{it.factura.observaciones}</div>
                          )}
                        </div>
                      </td>

                      {/* Estado general */}
                      <td className="py-2.5 px-2 text-center" data-testid={`alq-estado-${it.unidad_id}`}>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ring-1 ${gen.cls}`}>
                          {gen.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      <ObsDialog
        open={!!obsDialog}
        onOpenChange={(o) => !o && setObsDialog(null)}
        title={
          obsDialog?.tipo === 'rev' ? 'Observar monto de alquiler' :
          obsDialog?.tipo === 'fact' ? 'Observar factura propietario' : ''
        }
        initialObs={
          obsDialog?.tipo === 'rev' ? obsDialog?.unidad?.revision?.observacion :
          obsDialog?.tipo === 'fact' ? obsDialog?.unidad?.factura?.observaciones : ''
        }
        onSave={(obs) => {
          if (!obsDialog) return;
          const { tipo, unidad } = obsDialog;
          if (tipo === 'rev') setRevision(unidad.unidad_id, 'observado', obs);
          if (tipo === 'fact') patchFactura(unidad.unidad_id, { estado: 'observada', observaciones: obs });
        }}
      />
      {/* AlertDialog: confirmar cancelar aprobación de revisión */}
      <AlertDialog open={!!cancelAprobDialog} onOpenChange={(o) => !o && setCancelAprobDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar aprobación de esta factura?</AlertDialogTitle>
            <AlertDialogDescription>
              La revisión volverá al estado <strong>Sin revisar GC</strong> y podrás aprobarla u observarla nuevamente. La factura, archivo y observaciones se conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-cancel-aprob-btn"
              onClick={async () => {
                const uid = cancelAprobDialog;
                setCancelAprobDialog(null);
                if (uid) await setRevision(uid, 'pendiente');
              }}
            >
              Sí, cancelar aprobación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TabsContent>
  );
};

export default TabAlquilerMensualForesta;
